import { createContext, useContext, useEffect, useState } from 'react'
import { Box, Button, Card, Divider, Stack, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import { eventService } from '../../services/eventService'
import type { Session, Speaker, ContentItem, EventInfo } from '../../data/types'
import type { Block } from '../../services/pagesService'
import { useAppBase, appPath } from '../appBase'
import SessionCard from '../SessionCard'
import SafeHtml from '../SafeHtml'
import { blockStyleSx } from './blockStyle'
import { sponsorsService, type Sponsor } from '../../services/sponsorsService'
import { resolveAssetUrl } from '../../services/assetUrl'
import EventInfoSection from '../EventInfoSection'
import {
  hasEventInfoContent,
  resolveBlockSections,
  type EventInfoSections,
} from '../eventInfo'

/**
 * Optional pre-fetched event data for dynamic blocks. When provided (e.g. the
 * admin builder preview, which is scoped to a specific event), dynamic blocks
 * render from it instead of fetching via eventService (which resolves the
 * attendee's active/current event). When absent, blocks fetch live data.
 */
export interface BlockRenderData {
  sessions?: Session[]
  speakers?: Speaker[]
  content?: ContentItem[]
  /** The event, so the Event Info block can render logistics in admin preview. */
  event?: EventInfo | null
}

const RenderDataContext = createContext<BlockRenderData | null>(null)

/** Render a list of composed blocks (attendee side, or admin preview). */
export default function BlockRenderer({
  blocks,
  data,
}: {
  blocks: Block[]
  data?: BlockRenderData
}) {
  return (
    <RenderDataContext.Provider value={data ?? null}>
      <Stack spacing={4}>
        {blocks.map((b) => (
          <BlockView key={b.id} block={b} />
        ))}
      </Stack>
    </RenderDataContext.Provider>
  )
}

function cfg<T = string>(block: Block, key: string): T | undefined {
  return block.config?.[key] as T | undefined
}

function BlockView({ block }: { block: Block }) {
  const styleSx = blockStyleSx(block.config)
  const hasStyle = Object.keys(styleSx as object).length > 0
  const inner = <BlockInner block={block} />
  if (!hasStyle) return inner
  return <Box sx={styleSx}>{inner}</Box>
}

function BlockInner({ block }: { block: Block }) {
  const base = useAppBase()

  switch (block.blockType) {
    case 'hero':
      return <HeroBlock block={block} base={base} />
    case 'richtext':
    case 'html':
      return <HtmlBlock html={block.html ?? ''} />
    case 'image':
      return <ImageBlock block={block} />
    case 'button': {
      const action = (cfg(block, 'action') as string) ?? 'link'
      // Register/Sign-in action buttons map to in-app sub-paths (the email
      // renderer resolves these to absolute URLs; on a page they're relative).
      const url =
        action === 'register'
          ? 'register'
          : action === 'signin'
          ? 'signin'
          : cfg(block, 'url') ?? ''
      const label = cfg(block, 'label') ?? 'Open'
      const external = /^https?:\/\//i.test(url)
      return (
        <Box>
          <Button
            variant={(cfg(block, 'variant') as 'contained' | 'outlined') ?? 'contained'}
            {...(external
              ? { component: 'a', href: url, target: '_blank', rel: 'noopener noreferrer' }
              : { component: Link, to: appPath(base, url) })}
          >
            {label}
          </Button>
        </Box>
      )
    }
    case 'divider':
      return <Divider />
    case 'agenda':
      return <AgendaBlock block={block} />
    case 'speakers':
      return <SpeakersBlock block={block} base={base} />
    case 'content':
      return <ContentBlock block={block} base={base} />
    case 'columns':
      return <ColumnsBlock block={block} />
    case 'embed':
      return <EmbedBlock block={block} />
    case 'sponsors':
      return <SponsorsBlock block={block} />
    case 'eventinfo':
      return <EventInfoBlock block={block} />
    default:
      return null
  }
}

function SponsorsBlock({ block }: { block: Block }) {
  const [sponsors, setSponsors] = useState<Sponsor[]>([])
  useEffect(() => {
    sponsorsService.list().then(setSponsors).catch(() => setSponsors([]))
  }, [])
  if (sponsors.length === 0) return null
  // Group by tier, preserving the server's tier ordering.
  const groups: { tier: string; items: Sponsor[] }[] = []
  for (const s of sponsors) {
    const tier = s.tier || 'Sponsors'
    let g = groups.find((x) => x.tier === tier)
    if (!g) {
      g = { tier, items: [] }
      groups.push(g)
    }
    g.items.push(s)
  }
  return (
    <Box>
      {cfg(block, 'title') && <SectionTitle>{cfg(block, 'title')}</SectionTitle>}
      <Stack spacing={3}>
        {groups.map((g) => (
          <Box key={g.tier}>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              {g.tier}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                mt: 1,
              }}
            >
              {g.items.map((s) => {
                const inner = s.logoUrl ? (
                  <Box
                    component="img"
                    src={resolveAssetUrl(s.logoUrl)}
                    alt={s.name}
                    sx={{ maxHeight: 56, maxWidth: 160, objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="subtitle1" fontWeight={700}>
                    {s.name}
                  </Typography>
                )
                return (
                  <Card
                    key={s.id}
                    variant="outlined"
                    {...(s.websiteUrl
                      ? {
                          component: 'a',
                          href: s.websiteUrl,
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        }
                      : {})}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 140,
                      minHeight: 80,
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    {inner}
                  </Card>
                )
              })}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

function EventInfoBlock({ block }: { block: Block }) {
  // Prefer the event injected for admin preview (RenderDataContext); on the
  // attendee page no data is injected, so fetch it — mirroring AgendaBlock etc.
  const injected = useContext(RenderDataContext)
  const [event, setEvent] = useState<EventInfo | null>(injected?.event ?? null)
  useEffect(() => {
    if (injected) {
      setEvent(injected.event ?? null)
      return
    }
    eventService.getEvent().then(setEvent).catch(() => setEvent(null))
  }, [injected])

  if (!event) return null
  // Resolve sub-sections: per-section defaults (welcome + venue on) overlaid with
  // any explicitly-saved choices, so a default-on section the admin never toggled
  // isn't dropped (previously a config like {travel:true} hid welcome + venue).
  const configured = cfg<Record<string, boolean>>(block, 'sections')
  const sections: EventInfoSections = resolveBlockSections(configured)
  if (!hasEventInfoContent(event, sections)) return null
  return (
    <Box>
      {cfg(block, 'title') && <SectionTitle>{cfg(block, 'title')}</SectionTitle>}
      <EventInfoSection event={event} sections={sections} />
    </Box>
  )
}

function ColumnsBlock({ block }: { block: Block }) {
  const children = block.children ?? []
  // Group children by their config.column index (default 0).
  const colCount = Math.max(
    2,
    (cfg<number>(block, 'columnCount') as number) ||
      (children.reduce(
        (m, c) => Math.max(m, ((c.config?.column as number) ?? 0) + 1),
        0,
      ) || 2),
  )
  const cols: Block[][] = Array.from({ length: colCount }, () => [])
  children.forEach((c) => {
    const idx = Math.min((c.config?.column as number) ?? 0, colCount - 1)
    cols[idx].push(c)
  })
  // Flexbox (not MUI Grid) so there are no container negative margins that
  // would visually shift the row off-center inside the page container. Columns
  // share width evenly and stack on small screens.
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 3,
        width: '100%',
      }}
    >
      {cols.map((cellBlocks, i) => (
        <Box
          key={i}
          sx={{
            flex: {
              xs: '1 1 100%',
              sm: `1 1 calc(${100 / Math.min(colCount, 2)}% - 24px)`,
              md: `1 1 calc(${100 / colCount}% - ${(24 * (colCount - 1)) / colCount}px)`,
            },
            minWidth: 0,
          }}
        >
          <Stack spacing={2}>
            {cellBlocks
              .sort((a, b) => a.ordinal - b.ordinal)
              .map((cb) => (
                <BlockView key={cb.id} block={cb} />
              ))}
          </Stack>
        </Box>
      ))}
    </Box>
  )
}

function EmbedBlock({ block }: { block: Block }) {
  const embedUrl = cfg(block, 'embedUrl') ?? cfg(block, 'url') ?? ''
  if (!embedUrl) return null
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        pt: '56.25%', // 16:9
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: '#000',
      }}
    >
      <Box
        component="iframe"
        src={embedUrl}
        title="Embedded video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 0,
        }}
      />
    </Box>
  )
}

function HtmlBlock({ html }: { html: string }) {
  // Rendered via the single audited SafeHtml sink (DOMPurify + server-side nh3).
  return (
    <SafeHtml
      html={html}
      sx={{
        '& h1,& h2,& h3': { fontWeight: 700, mt: 1, mb: 1 },
        '& p': { mb: 1.5, lineHeight: 1.6 },
        '& img': { maxWidth: '100%', borderRadius: 2 },
        '& a': { color: 'primary.main' },
        '& ul,& ol': { pl: 3, mb: 1.5 },
        '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5 },
        '& td,& th': { border: '1px solid', borderColor: 'divider', p: '6px 10px' },
        '& .vc-color-primary': { color: '#0075BC' },
        '& .vc-color-accent': { color: '#1C5580' },
        '& .vc-color-muted': { color: '#51788F' },
        '& .vc-color-success': { color: '#2e7d32' },
        '& .vc-color-danger': { color: '#d32f2f' },
      }}
    />
  )
}

function HeroBlock({ block, base }: { block: Block; base: string }) {
  const title = cfg(block, 'title') ?? ''
  const subtitle = cfg(block, 'subtitle') ?? ''
  const image = cfg(block, 'image') ?? ''
  const ctaLabel = cfg(block, 'ctaLabel')
  const ctaHref = cfg(block, 'ctaHref')
  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 3, md: 5 },
        color: image ? '#fff' : 'text.primary',
        position: 'relative',
        overflow: 'hidden',
        background: image
          ? `linear-gradient(rgba(28,85,128,0.72),rgba(28,85,128,0.72)), url(${image}) center/cover`
          : 'linear-gradient(135deg, rgba(0,117,188,0.12), rgba(91,198,204,0.12))',
      }}
    >
      <Typography variant="h3" fontWeight={800} gutterBottom>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="h6" sx={{ opacity: 0.95, mb: 2, fontWeight: 400 }}>
          {subtitle}
        </Typography>
      )}
      {ctaLabel && ctaHref && (
        <Button variant="contained" component={Link} to={appPath(base, ctaHref)}>
          {ctaLabel}
        </Button>
      )}
    </Box>
  )
}

function ImageBlock({ block }: { block: Block }) {
  const url = cfg(block, 'url')
  const alt = cfg(block, 'alt') ?? ''
  const caption = cfg(block, 'caption')
  if (!url) return null
  return (
    <Box>
      <Box
        component="img"
        src={resolveAssetUrl(url)}
        alt={alt}
        sx={{ width: '100%', borderRadius: 3, display: 'block' }}
      />
      {caption && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {caption}
        </Typography>
      )}
    </Box>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
      {children}
    </Typography>
  )
}

function AgendaBlock({ block }: { block: Block }) {
  const injected = useContext(RenderDataContext)
  const [sessions, setSessions] = useState<Session[]>(injected?.sessions ?? [])
  const [speakers, setSpeakers] = useState<Speaker[]>(injected?.speakers ?? [])
  const limit = (cfg<number>(block, 'limit') as number) ?? 0
  useEffect(() => {
    if (injected) {
      setSessions(injected.sessions ?? [])
      setSpeakers(injected.speakers ?? [])
      return
    }
    eventService.getSessions().then(setSessions).catch(() => setSessions([]))
    eventService.getSpeakers().then(setSpeakers).catch(() => setSpeakers([]))
  }, [injected])
  const now = Date.now()
  let items = sessions.filter((s) => new Date(s.endsAt).getTime() >= now)
  if (limit > 0) items = items.slice(0, limit)
  if (items.length === 0) return null
  return (
    <Box>
      <SectionTitle>{cfg(block, 'title') ?? 'Agenda'}</SectionTitle>
      <Stack spacing={1.5}>
        {items.map((s) => (
          <SessionCard key={s.id} session={s} speakers={speakers} />
        ))}
      </Stack>
    </Box>
  )
}

function SpeakersBlock({ block, base }: { block: Block; base: string }) {
  const injected = useContext(RenderDataContext)
  const [speakers, setSpeakers] = useState<Speaker[]>(injected?.speakers ?? [])
  const featuredOnly = Boolean(cfg(block, 'featuredOnly'))
  useEffect(() => {
    if (injected) {
      setSpeakers(injected.speakers ?? [])
      return
    }
    eventService.getSpeakers().then(setSpeakers).catch(() => setSpeakers([]))
  }, [injected])
  const items = featuredOnly ? speakers.filter((s) => s.featured) : speakers
  if (items.length === 0) return null
  return (
    <Box>
      <SectionTitle>{cfg(block, 'title') ?? 'Speakers'}</SectionTitle>
      <Stack spacing={1.5}>
        {items.map((sp) => (
          <Card
            key={sp.id}
            variant="outlined"
            component={Link}
            to={appPath(base, `speakers/${sp.id}`)}
            sx={{
              p: 2,
              borderRadius: 3,
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                flexShrink: 0,
                overflow: 'hidden',
                bgcolor: sp.avatarColor || 'primary.main',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                backgroundImage: sp.photoUrl
                  ? `url(${resolveAssetUrl(sp.photoUrl)})`
                  : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {sp.photoUrl ? '' : sp.initials || sp.name.slice(0, 2).toUpperCase()}
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {sp.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[sp.title, sp.company].filter(Boolean).join(', ')}
              </Typography>
            </Box>
          </Card>
        ))}
      </Stack>
    </Box>
  )
}

function ContentBlock({ block, base }: { block: Block; base: string }) {
  const injected = useContext(RenderDataContext)
  const [items, setItems] = useState<ContentItem[]>(injected?.content ?? [])
  useEffect(() => {
    if (injected) {
      setItems(injected.content ?? [])
      return
    }
    eventService.getContent().then(setItems).catch(() => setItems([]))
  }, [injected])
  if (items.length === 0) return null
  return (
    <Box>
      <SectionTitle>{cfg(block, 'title') ?? 'Content'}</SectionTitle>
      <Stack spacing={1.5}>
        {items.map((c) => (
          <Card
            key={c.id}
            variant="outlined"
            component={Link}
            to={appPath(base, 'content')}
            sx={{
              p: 2,
              borderRadius: 3,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              {c.title}
            </Typography>
            {c.description && (
              <Typography variant="body2" color="text.secondary">
                {c.description}
              </Typography>
            )}
          </Card>
        ))}
      </Stack>
    </Box>
  )
}

export { HtmlBlock }
